import { ArrowRight, CheckCircle2, Plane } from "lucide-react";
import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { fadeUpItem, heroVisualReveal, staggerContainer } from "./homepageMotion";
import { SweepButton } from "./HomepagePrimitives";

type HomepageHeroSectionProps = {
  onRequestQuote?: () => void;
};

/**
 * Centered hero section for the TripboxCRM homepage.
 * @param props Optional quote CTA handler
 * @returns Hero section
 */
export const HomepageHeroSection = ({ onRequestQuote }: HomepageHeroSectionProps) => {
  const flightPathRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: flightPathProgress } = useScroll({
    target: flightPathRef,
    offset: ["start 75%", "end start"],
  });
  const planeLeft = useTransform(flightPathProgress, [0, 1], ["-8%", "108%"]);
  const planeTop = useTransform(flightPathProgress, [0, 0.5, 1], ["80%", "30%", "74%"]);
  const planeRotate = useTransform(flightPathProgress, [0, 0.5, 1], [30, 45, 70]);
  const planeOpacity = useTransform(flightPathProgress, [0, 0.08, 0.9, 1], [0, 0.92, 0.92, 0]);

  return (
    <section
      id="hero"
      className="relative overflow-hidden border-b border-gray-200 bg-white px-4 py-24 sm:px-6 lg:py-32"
    >
      <div
        aria-hidden="true"
        className="tb-animated-grid absolute inset-0 opacity-60 [background-image:radial-gradient(circle,_rgba(19,27,42,0.18)_1px,_transparent_1px)] [background-size:24px_24px] [mask-image:linear-gradient(to_bottom,black_0%,black_50%,transparent_100%)]"
      />
      <div className="mx-auto max-w-7xl">
        <motion.div
          className="relative z-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div className="mx-auto max-w-6xl text-center" variants={staggerContainer}>
            <motion.div
              variants={fadeUpItem}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#131B2A]/78 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
            >
              <CheckCircle2 className="tb-loop-icon h-4 w-4 text-(--brand-primary)" />
              Built for agencies, DMCs, and tour operators
            </motion.div>
            <motion.h1
              variants={fadeUpItem}
              className="mx-auto mt-6 max-w-[17ch] text-balance text-5xl font-semibold tracking-normal text-[#131B2A] sm:text-6xl lg:text-[4.8rem] lg:leading-tight"
            >
              The All-in-One CRM Built Exclusively for the Travel Trade
            </motion.h1>
            <motion.p
              variants={fadeUpItem}
              className="mx-auto mt-6 max-w-[60ch] text-base leading-relaxed text-gray-600 sm:text-lg"
            >
              Automate lead capture, build stunning itineraries in 60 seconds, and manage your entire tour operations
              from one deeply secure platform.
            </motion.p>
            <motion.div
              variants={fadeUpItem}
              className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <SweepButton
                href="#final-cta"
                tone="navy"
                className="min-h-12 px-6 no-underline"
                onClick={
                  onRequestQuote
                    ? (event) => {
                        event.preventDefault();
                        onRequestQuote();
                      }
                    : undefined
                }
              >
                Request Custom Pricing
                <ArrowRight className="tb-loop-arrow h-4 w-4" />
              </SweepButton>
              <button
                onClick={
                  onRequestQuote
                    ? (event) => {
                        event.preventDefault();
                        onRequestQuote();
                      }
                    : undefined
                }
                className="cursor-pointer inline-flex min-h-12 items-center justify-center rounded-lg border border-gray-200 bg-white px-6 text-sm font-semibold !text-[#131B2A] no-underline transition-all duration-150 hover:scale-95 hover:border-[#131B2A]/20 hover:bg-[#FBFBFB] hover:!text-[#131B2A] active:scale-95"
              >
                Book a Live Demo
              </button>
            </motion.div>
            <motion.div
              ref={flightPathRef}
              variants={fadeUpItem}
              aria-hidden="true"
              className="pointer-events-none relative left-1/2 mt-8 hidden h-32 w-screen -translate-x-1/2 md:block"
            >
              <svg
                className="h-full w-full overflow-visible"
                viewBox="0 0 1440 160"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M-24 120 Q 720 -12 1464 120"
                  stroke="#131B2A80"
                  strokeDasharray="8 12"
                  strokeLinecap="round"
                  strokeOpacity="0.24"
                  strokeWidth="1.35"
                />
              </svg>
              <motion.div
                className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center text-[#131B2A]"
                style={{ left: planeLeft, top: planeTop, opacity: planeOpacity }}
              >
                <motion.span className="flex origin-center" style={{ rotate: planeRotate }}>
                  <Plane className="h-9 w-9" />
                </motion.span>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            variants={heroVisualReveal}
            className="tb-scanline mx-auto mt-10 max-w-6xl rounded-xl border border-gray-200/50 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-100"
          >
            <div className="grid items-center gap-4 border-b border-gray-200 bg-[#FBFBFB] px-5 py-3 sm:grid-cols-[1fr_auto_1fr]">
              <div className="flex items-center gap-2">
                <span className="tb-loop-pulse h-3 w-3 rounded-full bg-[#ff5f57]" />
                <span className="tb-loop-pulse h-3 w-3 rounded-full bg-[#ffbd2e]" style={{ animationDelay: "0.24s" }} />
                <span className="tb-loop-pulse h-3 w-3 rounded-full bg-[#28c840]" style={{ animationDelay: "0.48s" }} />
              </div>
              <div>
                <p className="text-center text-xs font-medium uppercase tracking-[0.16em] text-[#131B2A]/48">
                  Command center
                </p>
                <p className="mt-1 text-center text-sm font-semibold text-[#131B2A]">TripboxCRM operations overview</p>
              </div>
              <div className="justify-self-end rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-[#131B2A]">
                Secure workspace
              </div>
            </div>

            <div className="grid gap-0 lg:grid-cols-[1.35fr_0.85fr]">
              <div className="border-b border-gray-200 p-5 lg:border-b-0 lg:border-r">
                <div className="grid gap-4 md:grid-cols-[0.9fr_1.1fr]">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-medium text-[#131B2A]/44">Sales pipeline</p>
                    <p className="mt-2 text-3xl font-semibold text-[#131B2A]">$482k</p>
                    <div className="mt-5 space-y-3">
                      {[
                        { label: "Qualified leads", width: "w-[82%]", tone: "bg-[#131B2A]" },
                        { label: "Proposal sent", width: "w-[61%]", tone: "bg-[#475569]" },
                        { label: "Deposit secured", width: "w-[44%]", tone: "bg-(--brand-primary)" },
                      ].map((item) => (
                        <div key={item.label}>
                          <div className="flex items-center justify-between text-xs text-[#131B2A]/54">
                            <span>{item.label}</span>
                            <span>Live</span>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-gray-100">
                            <div className={`tb-progress-breathe h-2 rounded-full ${item.tone} ${item.width}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-[#131B2A]/44">Lead activity</p>
                        <p className="mt-2 text-sm font-semibold text-[#131B2A]">Incoming email leads and quotes</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-[#131B2A]/70">
                        24h
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-8 items-end gap-2">
                      {[28, 42, 36, 64, 52, 73, 58, 80].map((height, index) => (
                        <div key={height} className="space-y-2">
                          <motion.div
                            className={`w-full rounded-t-md ${index === 7 ? "bg-(--brand-primary)" : "bg-[#131B2A]"} `}
                            style={{ height: `${height}px` }}
                            initial={{ scaleY: 0.2, transformOrigin: "bottom" }}
                            animate={{ scaleY: 1 }}
                            transition={{ delay: 0.7 + index * 0.05, type: "spring", stiffness: 260, damping: 24 }}
                          />
                          <div className="h-1 rounded-full bg-gray-200" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-[1.08fr_0.92fr]">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-[#131B2A]/44">Incoming email leads</p>
                        <p className="mt-1 text-sm font-semibold text-[#131B2A]">Mailbox triage</p>
                      </div>
                      <span className="tb-loop-pulse rounded-md border border-(--brand-primary)/30 bg-(--brand-primary)/10 px-2 py-1 text-xs font-semibold text-[#131B2A]">
                        14 new
                      </span>
                    </div>
                    <div className="mt-4 space-y-3">
                      {[
                        { name: "Family Europe inquiry", source: "Gmail", stage: "New" },
                        { name: "Bali group departure", source: "Website", stage: "Assigned" },
                        { name: "Dubai luxury quote", source: "Meta", stage: "Follow-up" },
                      ].map((lead) => (
                        <div
                          key={lead.name}
                          className="grid grid-cols-[1fr_auto] gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3"
                        >
                          <div>
                            <p className="text-sm font-medium text-[#131B2A]">{lead.name}</p>
                            <p className="mt-1 text-xs text-gray-500">{lead.source}</p>
                          </div>
                          <span className="self-start rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600">
                            {lead.stage}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-[#131B2A]/44">Active itineraries</p>
                        <span className="text-xs font-semibold text-[#131B2A]">32</span>
                      </div>
                      <div className="mt-4 space-y-2">
                        {["Draft", "Client review", "Confirmed"].map((stage, index) => (
                          <div key={stage} className="flex items-center gap-3">
                            <div className="h-2 flex-1 rounded-full bg-gray-100">
                              <div
                                className={`tb-progress-breathe h-2 rounded-full ${index === 2 ? "bg-(--brand-primary)" : "bg-[#131B2A]"}`}
                                style={{ width: `${[48, 72, 58][index]}%` }}
                              />
                            </div>
                            <span className="w-20 text-right text-xs text-gray-500">{stage}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <p className="text-xs font-medium text-[#131B2A]/44">Payment schedules</p>
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {[
                          { label: "Due", value: "18" },
                          { label: "Paid", value: "44" },
                          { label: "Overdue", value: "03" },
                        ].map((item) => (
                          <div key={item.label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <p className="text-lg font-semibold text-[#131B2A]">{item.value}</p>
                            <p className="mt-1 text-xs text-gray-500">{item.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="space-y-4">
                  <div className="rounded-xl border border-[#131B2A]/10 bg-[#131B2A] p-4 text-white">
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/48">CRM workflows</p>
                    <div className="mt-4 space-y-3">
                      {["Email lead capture", "Consultant assignment", "Itinerary PDF generated"].map((item) => (
                        <div key={item} className="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      { label: "Lead response", value: "4m" },
                      { label: "Conversion rate", value: "63%" },
                    ].map((item) => (
                      <div key={item.label} className="rounded-xl border border-gray-200 bg-white p-4">
                        <p className="text-xs font-medium text-[#131B2A]/44">{item.label}</p>
                        <p className="mt-2 text-2xl font-semibold tracking-normal text-[#131B2A]">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between text-xs font-medium text-[#131B2A]/48">
                      <span>Upcoming departures</span>
                      <span>07 active</span>
                    </div>
                    <div className="mt-4 space-y-3">
                      {["Bali incentive group", "Bhutan retreat", "Dubai private departure"].map((item, index) => (
                        <div
                          key={item}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs font-semibold text-[#131B2A]">
                              0{index + 1}
                            </div>
                            <span className="text-sm font-medium text-[#131B2A]">{item}</span>
                          </div>
                          <div className="tb-loop-pulse h-2 w-2 rounded-full bg-(--brand-primary)" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
